import CloseIcon from '@mui/icons-material/Close'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import AssessmentIcon from '@mui/icons-material/Assessment'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import CodeIcon from '@mui/icons-material/Code'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import Alert from '@mui/material/Alert'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import Fade from '@mui/material/Fade'
import IconButton from '@mui/material/IconButton'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import { createVoxistTheme } from './theme/theme'
import useMediaQuery from '@mui/material/useMediaQuery'
import { initializeApp } from 'firebase/app'
import {
  confirmPasswordReset,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup
} from 'firebase/auth'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { TtsProvider } from './contexts/TtsContext'
import {
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom'
import './App.css'
import './i18n'
import ErrorBoundary from './components/ErrorBoundary'
import MobileDrawer from './components/MobileDrawer'
import { useLanguageSync } from './hooks/usePageMeta'
import { tokens } from './theme/tokens'

import LogoAsr from './assets/logo_voxist_asr.svg'

// Theme mode context for manual dark/light toggle
const ThemeModeContext = createContext<{ toggleTheme: () => void }>({ toggleTheme: () => {} })
const useThemeMode = () => useContext(ThemeModeContext)

import { Avatar, Menu, MenuItem, Typography, Button, Divider } from '@mui/material'
import Reset from 'routes/reset'
import SendReset from 'routes/sendReset'
import Form from './routes/form'
import Home from './routes/home'
import SpeechPage from './routes/speech'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import DeveloperDocs from './routes/docs'
import AdminPage from './routes/admin'
import BillingModal from './components/BillingModal'
import UsageModal from './components/UsageModal'
import ApiKeysModal from './components/ApiKeysModal'
import { useTranslation } from 'react-i18next'
import VoiceGallery from './routes/voices'

const API_BASE = window.__ENV__?.API_BASE || 'http://localhost:3000'

const AuthContext = createContext(null)

const AlertContext = createContext(null)

// Dev-only auth bypass: set REACT_APP_DEV_BYPASS_AUTH=true to skip login
const DEV_BYPASS_AUTH = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_BYPASS_AUTH === 'true'

const DEV_USER = DEV_BYPASS_AUTH ? {
  uid: 'dev_local_user',
  email: 'dev@voxist.local',
  displayName: 'Dev User',
  token: 'dev-token-bypass',
  getIdToken: async () => 'dev-token-bypass',
} : null

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState<any>(DEV_USER)
  const [isAdmin, setIsAdmin] = useState(DEV_BYPASS_AUTH)
  const [adminLoading, setAdminLoading] = useState(false)

  // Fetch user profile to check admin status
  const fetchUserProfile = async (token: string) => {
    setAdminLoading(true)
    try {
      const response = await fetch(`${API_BASE}/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (response.ok) {
        const profile = await response.json()
        setIsAdmin(profile.role === 'admin')
      } else {
        setIsAdmin(false)
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error)
      setIsAdmin(false)
    } finally {
      setAdminLoading(false)
    }
  }

  // Load user from session storage on init
  useEffect(() => {
    const storedToken = sessionStorage.getItem('access_token')
    const storedUser = sessionStorage.getItem('user')

    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser)
        setUser({
          ...userData,
          token: storedToken,
          getIdToken: async () => storedToken
        })
        // Fetch admin status for stored session
        fetchUserProfile(storedToken)
      } catch (error) {
        console.error('Failed to parse stored user:', error)
        sessionStorage.removeItem('access_token')
        sessionStorage.removeItem('refresh_token')
        sessionStorage.removeItem('user')
      }
    }
  }, [])
  const navigate = useNavigate()

  const { setAlert } = useAlert()
  const { t } = useTranslation()

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (userOb) => {
      if (userOb) {
        // console.log(userOb)
        setUser(userOb)
        setAlert({ type: 'success', text: t('success') })
        // Fetch admin status for Firebase users
        try {
          const token = await userOb.getIdToken()
          fetchUserProfile(token)
        } catch (error) {
          console.error('Failed to get token for admin check:', error)
        }
      } else {
        setUser(null)
        setIsAdmin(false)
      }
    })
    return () => {
      unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getToken = async () => {
    try {
      if (user?.token) {
        // Internal database authentication
        return user.token
      } else if (auth.currentUser) {
        // Firebase authentication
        const token = await auth.currentUser.getIdToken()
        return token
      }
      throw new Error('No authenticated user')
    } catch (error) {
      setAlert({ type: 'error', text: t('error') })
      throw error
    }
  }
  const sendReset = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email)
      navigate('/')
    } catch (error) {
      setAlert({ type: 'error', text: t('error') })
    }
  }
  const resetPass = async (oob, password) => {
    try {
      await confirmPasswordReset(auth, oob, password)
      navigate('/')
    } catch (error) {
      setAlert({ type: 'error', text: t('error') })
    }
  }

  const signIn = async (email, password) => {
    try {
      // Use internal database authentication instead of Firebase
      const response = await fetch(`${window.__ENV__?.API_BASE || 'http://localhost:3000'}/oauth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          grant_type: 'password',
          username: email,
          password: password,
          scope: 'self_manage transcription_send billing_manage',
        }),
      })

      if (!response.ok) {
        throw new Error('Authentication failed')
      }

      const tokenData = await response.json()
      
      // Store tokens in session storage
      sessionStorage.setItem('access_token', tokenData.access_token)
      sessionStorage.setItem('refresh_token', tokenData.refresh_token)
      
      // Create a pseudo user object compatible with Firebase user structure
      const pseudoUser = {
        uid: `internal_${email}`,
        email: email,
        token: tokenData.access_token,
        refresh_token: tokenData.refresh_token,
        getIdToken: async () => tokenData.access_token
      }
      
      // Store user data in session storage (without tokens to avoid duplication)
      const userForStorage = {
        uid: pseudoUser.uid,
        email: pseudoUser.email
      }
      sessionStorage.setItem('user', JSON.stringify(userForStorage))
      
      setUser(pseudoUser)
      setAlert({ type: 'success', text: t('success') })
      return pseudoUser
    } catch (error) {
      setAlert({ type: 'error', text: t('error') })
      throw error
    }
  }

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider()
      return await signInWithPopup(auth, provider)
    } catch (error) {
      setAlert({ type: 'error', text: t('error') })
    }
  }

  const signInWithMicrosoft = async () => {
    try {
      const provider = new OAuthProvider('microsoft.com')
      return await signInWithPopup(auth, provider)
    } catch (error) {
      setAlert({ type: 'error', text: t('error') })
    }
  }

  const signOut = async () => {
    if (user?.token) {
      // Internal database authentication - clear local state and session storage
      sessionStorage.removeItem('access_token')
      sessionStorage.removeItem('refresh_token')
      sessionStorage.removeItem('user')
      setUser(null)
      setIsAdmin(false)
    } else {
      // Firebase authentication
      await auth.signOut()
      setIsAdmin(false)
    }
  }

  const value = { user, isAdmin, adminLoading, signIn, signInWithGoogle, signInWithMicrosoft, signOut, getToken, sendReset, resetPass }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

const useAuth = () => useContext(AuthContext)

const useQuery = () => {
  const { search } = useLocation()

  return useMemo(() => new URLSearchParams(search), [search])
}
const RequireAuth = ({ children }) => {
  const auth = useAuth()
  const location = useLocation()

  if (!auth.user) {
    return <Navigate to="/" state={{ from: location }} replace />
  }

  return children
}

const CheckAuth = ({ children }) => {
  const auth = useAuth()
  const location = useLocation()

  if (auth.user) {
    return <Navigate to="/speech/asr" state={{ from: location }} replace />
  }

  return children
}

const RequireAdmin = ({ children }) => {
  const auth = useAuth()
  const location = useLocation()

  if (!auth.user) {
    return <Navigate to="/" state={{ from: location }} replace />
  }

  if (!auth.isAdmin) {
    return <Navigate to="/speech/asr" state={{ from: location }} replace />
  }

  return children
}

const AlertProvider = ({ children }) => {
  const [alert, setAlert] = useState(null)
  const value = { alert, setAlert }

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>
}

const useAlert = () => useContext(AlertContext)

const ThemeToggleButton = () => {
  const theme = useTheme()
  const { toggleTheme } = useThemeMode()
  const isDark = theme.palette.mode === 'dark'
  return (
    <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
      <IconButton
        onClick={toggleTheme}
        aria-label="Toggle theme"
        sx={{
          color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
          '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)' }
        }}
      >
        {isDark ? <LightModeIcon /> : <DarkModeIcon />}
      </IconButton>
    </Tooltip>
  )
}

const Layout = () => {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [anchorEl, setAnchorEl] = useState(null)
  const [billingModalOpen, setBillingModalOpen] = useState(false)
  const [usageModalOpen, setUsageModalOpen] = useState(false)
  const [apiKeysModalOpen, setApiKeysModalOpen] = useState(false)
  const { t } = useTranslation()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { alert, setAlert } = useAlert()

  useEffect(() => {
    let timeout = null
    if (alert) {
      timeout = setTimeout(() => setAlert(null), 5000)
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert])

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <AppBar position="sticky" elevation={0}>
      <Toolbar
        style={{
          backgroundColor: 'transparent',
          padding: 16,
          alignItems: 'center',
          justifyContent:
            location?.pathname !== '/home' ? 'space-between' : 'center',
          alignContent: 'center'
        }}
      >
{location?.pathname !== '/' && (
          <Box display="flex" alignItems="center" gap={2}>
            {isMobile && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="Open navigation"
                onClick={() => setDrawerOpen(true)}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Tooltip title="Home">
              <Link
                to="/"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignContent: 'center',
                  textDecoration: 'none'
                }}
              >
                <img src={LogoAsr} style={{ width: 54, height: 54 }} alt="Voxist ASR home" />
                <Typography
                  variant="h6"
                  sx={{
                    color: (theme) => theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary,
                    marginLeft: 10,
                    textDecoration: 'none'
                  }}
                >
                  {t('appTitle')}
                </Typography>
              </Link>
            </Tooltip>

            {auth?.user && !isMobile && (
              <Box component="nav" aria-label="Main navigation" display="flex" alignItems="center" gap={2}>
                <Button
                  onClick={() => navigate('/speech')}
                  startIcon={<RecordVoiceOverIcon />}
                  aria-current={location.pathname.startsWith('/speech') ? 'page' : undefined}
                  sx={(theme) => ({
                    color: location.pathname.startsWith('/speech')
                      ? (theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary)
                      : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary),
                    backgroundColor: location.pathname.startsWith('/speech')
                      ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)')
                      : 'transparent',
                    textTransform: 'none',
                    fontWeight: 700,
                    '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.04)' },
                  })}
                >
                  Speech
                </Button>
                <Button
                  onClick={() => navigate('/voices')}
                  startIcon={<GraphicEqIcon />}
                  aria-current={location.pathname.startsWith('/voices') ? 'page' : undefined}
                  sx={(theme) => ({
                    color: location.pathname.startsWith('/voices')
                      ? (theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary)
                      : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary),
                    backgroundColor: location.pathname.startsWith('/voices')
                      ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)')
                      : 'transparent',
                    textTransform: 'none',
                    fontWeight: 700,
                    '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.04)' },
                  })}
                >
                  Voices
                </Button>
                <Button
                  onClick={() => navigate('/docs')}
                  startIcon={<CodeIcon />}
                  aria-current={location.pathname.startsWith('/docs') ? 'page' : undefined}
                  sx={(theme) => ({
                    color: location.pathname.startsWith('/docs')
                      ? (theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary)
                      : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary),
                    backgroundColor: location.pathname.startsWith('/docs')
                      ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)')
                      : 'transparent',
                    textTransform: 'none',
                    fontWeight: 700,
                    '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.04)' },
                  })}
                >
                  Docs
                </Button>
              </Box>
            )}
          </Box>
        )}

        <Box display="flex" alignItems="center" gap={1}>
          <ThemeToggleButton />
          {auth?.user && (
            <Box>
              <Tooltip title={t('userMenu')}>
                <IconButton onClick={handleMenuOpen} aria-label={t('userMenu')} aria-haspopup="true" aria-expanded={Boolean(anchorEl)}>
                  <Avatar
                    sx={{ bgcolor: 'transparent', background: tokens.gradients.primary, color: '#fff', fontWeight: 700, fontSize: '0.875rem' }}
                  >
                    {auth?.user?.displayName
                      ? auth.user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                      : auth?.user?.email?.[0]?.toUpperCase() || '?'}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem disabled sx={{ opacity: 1 }}>
                  <Typography variant="body2" color="text.primary" fontWeight="500">
                    {auth?.user?.email}
                  </Typography>
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => {
                    setBillingModalOpen(true)
                    handleMenuClose()
                  }}
                >
                  <AccountBalanceWalletIcon sx={{ mr: 2 }} />
                  {t('billing')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setUsageModalOpen(true)
                    handleMenuClose()
                  }}
                >
                  <AssessmentIcon sx={{ mr: 2 }} />
                  {t('usage')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setApiKeysModalOpen(true)
                    handleMenuClose()
                  }}
                >
                  <VpnKeyIcon sx={{ mr: 2 }} />
                  {t('apiKeys')}
                </MenuItem>
                {auth?.isAdmin && (
                  <>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        navigate('/admin')
                        handleMenuClose()
                      }}
                    >
                      <AdminPanelSettingsIcon sx={{ mr: 2 }} />
                      {t('admin')}
                    </MenuItem>
                  </>
                )}
                <MenuItem
                  onClick={() => {
                    auth.signOut(() => navigate('/'))
                    handleMenuClose()
                  }}
                >
                  <LogoutIcon sx={{ mr: 2 }} />
                  {t('signOut')}
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>
      </Toolbar>
    </AppBar>
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        user={auth?.user}
        isAdmin={auth?.isAdmin}
        onNavigate={(path) => { navigate(path); setDrawerOpen(false); }}
        onBillingOpen={() => { setBillingModalOpen(true); setDrawerOpen(false); }}
        onUsageOpen={() => { setUsageModalOpen(true); setDrawerOpen(false); }}
        onApiKeysOpen={() => { setApiKeysModalOpen(true); setDrawerOpen(false); }}
        onSignOut={() => { auth.signOut(); setDrawerOpen(false); }}
        currentPath={location.pathname}
      />
      <Box component="main" id="main-content" sx={{ flexGrow: 1 }}>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </Box>
      <Box sx={{ bottom: 0, position: 'fixed', right: 0, zIndex: 1001 }} role="status" aria-live="polite" aria-atomic="true">
        <Fade in={Boolean(alert)} unmountOnExit>
          <Alert
            severity={alert?.type}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setAlert(null)
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{ mb: 2 }}
          >
            {alert?.text}
          </Alert>
        </Fade>
      </Box>
      <BillingModal open={billingModalOpen} onClose={() => setBillingModalOpen(false)} />
      <UsageModal open={usageModalOpen} onClose={() => setUsageModalOpen(false)} />
      <ApiKeysModal open={apiKeysModalOpen} onClose={() => setApiKeysModalOpen(false)} />
    </>
  )
}

// Firebase web config — these keys are public by design (shipped in client JS bundle).
// They are protected by Firebase security rules, not by being secret.
// Environment variables allow per-environment overrides; fallbacks are the production project.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyCQdH1EsUt1VIYm5CyT8K2DhHbntFc7aw0',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'asr-voxist.firebaseapp.com',
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL || 'https://asr-voxist-default-rtdb.europe-west1.firebasedatabase.app',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'asr-voxist',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'asr-voxist.appspot.com',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '705011226493',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:705011226493:web:cfd71e448889ee055cde14',
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || 'G-K14CB3Q7DV',
}

const app = initializeApp(firebaseConfig)

const auth = getAuth(app)

export { useAlert, useAuth, useQuery, useThemeMode }

const App = () => {
  useLanguageSync()
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
  const [mode, setMode] = useState<'light' | 'dark'>(prefersDarkMode ? 'dark' : 'light')

  // Sync with system preference unless user has manually toggled
  const [userOverride, setUserOverride] = useState(false)
  useEffect(() => {
    if (!userOverride) {
      setMode(prefersDarkMode ? 'dark' : 'light')
    }
  }, [prefersDarkMode, userOverride])

  const toggleTheme = useCallback(() => {
    setUserOverride(true)
    setMode(prev => prev === 'dark' ? 'light' : 'dark')
  }, [])

  const theme = useMemo(() => createVoxistTheme(mode), [mode])
  const themeModeValue = useMemo(() => ({ toggleTheme }), [toggleTheme])

  return (
    <AlertProvider>
      <AuthProvider>
        <ThemeModeContext.Provider value={themeModeValue}>
        <ThemeProvider theme={theme}>
          <TtsProvider>
          <CssBaseline />
          <Routes>
            <Route element={<Layout />}>
              <Route
                path="/"
                element={
                  <CheckAuth>
                    <Home />
                  </CheckAuth>
                }
              />
              <Route
                path="/resetLink"
                element={
                  <CheckAuth>
                    <SendReset />
                  </CheckAuth>
                }
              />
              <Route
                path="/reset"
                element={
                  <CheckAuth>
                    <Reset />
                  </CheckAuth>
                }
              />

              <Route path="/form" element={<Navigate to="/speech/asr" replace />} />

              <Route
                path="/speech/*"
                element={
                  <RequireAuth>
                    <SpeechPage />
                  </RequireAuth>
                }
              />

              <Route
                path="/voices"
                element={
                  <RequireAuth>
                    <VoiceGallery />
                  </RequireAuth>
                }
              />

              <Route
                path="/docs"
                element={
                  <RequireAuth>
                    <DeveloperDocs />
                  </RequireAuth>
                }
              ></Route>

              <Route
                path="/admin"
                element={
                  <RequireAdmin>
                    <AdminPage />
                  </RequireAdmin>
                }
              ></Route>
            </Route>
          </Routes>
          </TtsProvider>
        </ThemeProvider>
        </ThemeModeContext.Provider>
      </AuthProvider>
    </AlertProvider>
  )
}

export default App
